import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  useMediaQuery,
  useTheme,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import axios from "axios";
import {
  useDeleteCustomerMutation,
  useDeleteWorkOrderMutation,
  useGetCompanyQuery,
  useGetCustomerQuery,
  useGetWorkOrderQuery,
  useGetAllInvoiceQuery,
  useCreateEmailMutation,
  useSendQuoteEmailMutation,
  useConvertToInvoiceMutation,
} from "../../../Api/attoDeskApi";
import {
  ICompanyDetails,
  IQuoteWorkOrder,
  IInvoice,
} from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate, Link } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import CustomCheckbox from "../../../Components/Checkbox.tsx";
import Pagination from "../../../Components/Pagination";
import { WorkOrderStatus } from "../../../Core/Enum/enum";
import { fmtCustomerName } from '../../../Core/utils/nameUtils';
import dayjs from "dayjs";
import DescriptionIcon from "@mui/icons-material/Description";
import ViewWorkOrder from "../../CustomerTransaction/Components/ViewWorkOrder";
import CloseIcon from "@mui/icons-material/Close";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";

// ─── Status helpers ───────────────────────────────────────────────────────────

const getStatusLabel = (statusCode: number, isConverted?: boolean): string => {
  if (isConverted) return "Converted";
  switch (statusCode) {
    case WorkOrderStatus.Paid:      return "Confirmed";
    case WorkOrderStatus.Cancel:    return "Cancelled";
    case WorkOrderStatus.Pending:   return "Sent";
    case WorkOrderStatus.Initiated: return "Draft";
    default:                        return "Draft";
  }
};

const getStatusColor = (statusCode: number, isConverted?: boolean): string => {
  if (isConverted) return "#7c3aed";
  switch (statusCode) {
    case WorkOrderStatus.Paid:      return "#16a34a";
    case WorkOrderStatus.Cancel:    return "#dc2626";
    case WorkOrderStatus.Pending:   return "#2563eb";
    case WorkOrderStatus.Initiated: return "#64748b";
    default:                        return "#64748b";
  }
};

const getStatusBg = (statusCode: number, isConverted?: boolean): string => {
  if (isConverted) return "#f5f3ff";
  switch (statusCode) {
    case WorkOrderStatus.Paid:      return "#dcfce7";
    case WorkOrderStatus.Cancel:    return "#fee2e2";
    case WorkOrderStatus.Pending:   return "#dbeafe";
    case WorkOrderStatus.Initiated: return "#f1f5f9";
    default:                        return "#f1f5f9";
  }
};

// ─── Shared cell sx ──────────────────────────────────────────────────────────

const headerCellSx = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "#64748b",
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  py: 1.5,
  px: 2,
  whiteSpace: "nowrap" as const,
};

const bodyCellSx = {
  borderBottom: "1px solid #f1f5f9",
  py: 1.25,
  px: 2,
  verticalAlign: "middle" as const,
};

const actionBtnSx = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  color: "#64748b",
  backgroundColor: "#f1f5f9",
  "&:hover": { backgroundColor: "#e2e8f0", color: "#1e293b" },
};

// ─── Component ───────────────────────────────────────────────────────────────

const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedCoursing, setSelectedCoursing] = useState<IQuoteWorkOrder | null>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IQuoteWorkOrder | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [invoiceList, setInvoiceList] = useState<IInvoice[]>([]);
  const [quoteId, setQuoteId] = useState("");
  const [openViewQuote, setOpenViewQuote] = useState(false);

  // Email dialog
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailRow, setEmailRow] = useState<IQuoteWorkOrder | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  // Convert to Invoice dialog
  const [openConvertDialog, setOpenConvertDialog] = useState(false);
  const [convertRow, setConvertRow] = useState<IQuoteWorkOrder | null>(null);
  const [convertPoNumber, setConvertPoNumber] = useState("");
  const [convertPoDocFile, setConvertPoDocFile] = useState<File | null>(null);
  const [convertHasPo, setConvertHasPo] = useState<boolean | null>(null);
  const [convertLoading, setConvertLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const capitalize = (str: string | null | undefined): string => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const formatAmt = (amount: any): string => {
    const parsed = parseFloat(amount);
    return !isNaN(parsed) ? `$${parsed.toFixed(2)}` : "$0.00";
  };

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const { data, isLoading, isError, refetch } = useGetWorkOrderQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  const { data: invoiceData, isLoading: invoiceLoading, refetch: invoiceRefetch } =
    useGetAllInvoiceQuery({
      searchText: "",
      id: "",
      quoteWorkOrderCustomerId: "",
      quoteWorkOrderCustomerProjectId: "",
    });

  const { data: companyData } = useGetCompanyQuery({ searchText: "", id: "" });

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (dateFilter !== "custom") {
      let start = "";
      let end = dayjs().format("YYYY-MM-DD");
      switch (dateFilter) {
        case "today":      start = end; break;
        case "yesterday":  start = end = dayjs().subtract(1, "day").format("YYYY-MM-DD"); break;
        case "this_week":  start = dayjs().startOf("week").format("YYYY-MM-DD"); break;
        case "last_week":
          start = dayjs().subtract(1, "week").startOf("week").format("YYYY-MM-DD");
          end   = dayjs().subtract(1, "week").endOf("week").format("YYYY-MM-DD");
          break;
        case "this_month": start = dayjs().startOf("month").format("YYYY-MM-DD"); break;
        case "last_month":
          start = dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
          end   = dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
          break;
        default: break;
      }
      setStartDate(start);
      setEndDate(end);
    }
  }, [dateFilter]);

  useEffect(() => {
    if (invoiceList.length === 0) invoiceRefetch();
  }, [invoiceList, invoiceRefetch]);

  useEffect(() => {
    if (invoiceData?.data) {
      const arr = Array.isArray(invoiceData.data) ? invoiceData.data : [];
      setInvoiceList(arr);
    }
  }, [invoiceData]);

  // ─── Derived data ───────────────────────────────────────────────────────────

  const coursings: IQuoteWorkOrder[] = Array.isArray(data?.data)
    ? (data?.data as IQuoteWorkOrder[])
    : [];

  const filteredCoursings = coursings.filter((row) => {
    // Hide terminated quotes
    if (row.quoteWorkOrderStatus === WorkOrderStatus.Cancel) return false;
    // Hide quotes already converted to invoice
    const matchedInvoice = invoiceList.find(
      (i) => String(i.id) === String(row.invoiceId) || String((i as any).work_order_id) === String(row.id)
    );
    if (row.isInvoice || !!matchedInvoice) return false;

    const matchesDate = !dateFilter || !startDate || (() => {
      const rowDate = new Date(row.created_at).toLocaleDateString("en-CA");
      const s = new Date(startDate).toLocaleDateString("en-CA");
      const e = endDate ? new Date(endDate).toLocaleDateString("en-CA") : s;
      return rowDate >= s && rowDate <= e;
    })();
    const matchesIssue =
      !selectedCompany ||
      (Array.isArray(row.company)
        ? row.company.some((c: any) => String(c.id) === String(selectedCompany))
        : String(row.company?.id) === String(selectedCompany));

    // Client-side full-text search: quote no, invoice no, client name, issued by
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = !q || (() => {
      const inv = invoiceList.find((i) => String(i.id) === String(row.invoiceId));
      const invoiceNo = ((inv as any)?.InvoiceNo || "").toLowerCase();
      const quoteNo = (row.quoteWorkOrderNo || "").toLowerCase();
      const clientFirst = (row.customer?.customerFirstName || "").toLowerCase();
      const clientLast = (row.customer?.customerLastName || "").toLowerCase();
      const clientCompany = (row.customer?.customerCompanyName || "").toLowerCase();
      const issuedBy = (row.company?.companyName || "").toLowerCase();
      return (
        quoteNo.includes(q) ||
        invoiceNo.includes(q) ||
        clientFirst.includes(q) ||
        clientLast.includes(q) ||
        `${clientFirst} ${clientLast}`.includes(q) ||
        clientCompany.includes(q) ||
        issuedBy.includes(q)
      );
    })();

    return matchesDate && matchesIssue && matchesSearch;
  });

  // ─── Summary stats ──────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    let totalAmount = 0;
    let paidAmount  = 0;
    let openCount   = 0;
    filteredCoursings.forEach((row) => {
      const inv   = invoiceList.find((i) => String(i.id) === String(row.invoiceId));
      const grand = parseFloat(((inv as any)?.quoteWorkOrderSaleGrandTotal || row.quoteWorkOrderSaleGrandTotal) || "0");
      const paid  = parseFloat(String(row.amount_paid ?? (inv as any)?.amount_paid ?? 0));
      const status = inv ? (inv as any).quoteWorkOrderStatus : row.quoteWorkOrderStatus;
      totalAmount += grand;
      paidAmount  += paid;
      if (status === WorkOrderStatus.Pending || status === WorkOrderStatus.Initiated) openCount++;
    });
    return { totalAmount, paidAmount, openCount, totalCount: filteredCoursings.length };
  }, [filteredCoursings, invoiceList]);

  // ─── PDF Report Download ─────────────────────────────────────────────────────

  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (startDate)          params.start_date = startDate;
      if (endDate)            params.end_date = endDate;
      if (selectedCompany)    params.company_id = selectedCompany;

      const response = await axios.get(`${API_URL}/pdf/quotes-report`, {
        params,
        responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `quotes-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      // If the backend returned a JSON error body, surface it
      let msg = "Failed to generate PDF report. Please try again.";
      try {
        const text = await err?.response?.data?.text?.();
        const parsed = text ? JSON.parse(text) : null;
        if (parsed?.message) msg = parsed.message;
      } catch { /* ignore parse failure, keep default message */ }
      showErrorMessage(msg);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExportToExcel = () => {
    const data = filteredCoursings.map((row, i) => {
      const sales: any[] = Array.isArray(row.quoteWorkOrderSale) ? row.quoteWorkOrderSale : [];
      const tripParts = sales.map((sale: any) => {
        const firstPickup = Array.isArray(sale.pickups) && sale.pickups.length > 0 ? sale.pickups[0] : null;
        const tripDate    = firstPickup?.dateTime || sale.departureDateTime;
        const from        = (sale.transferTypeFrom || firstPickup?.city || "").trim();
        const to          = (sale.transferTypeTo || "").trim();
        const busType     = sale.productShortDescription || sale.productName || "";
        const tripType    = String(sale.transferType) === "1" ? "Round Trip" : "One Way";
        const parts: string[] = [];
        if (tripDate && dayjs(tripDate).isValid()) parts.push(dayjs(tripDate).format("MMM D, YYYY h:mm A"));
        if (from || to) parts.push([from, to].filter(Boolean).join(" → "));
        if (busType)    parts.push(busType);
        parts.push(tripType);
        return parts.join(" | ");
      });

      return {
        "#":            i + 1,
        "Quote No":     row.quoteWorkOrderNo || "",
        "Date":         row.quoteWorkOrderIssueDate ? dayjs(row.quoteWorkOrderIssueDate).format("DD MMM YYYY") : "",
        "Client":       row.customer ? (`${row.customer.customerFirstName || ""} ${row.customer.customerLastName || ""}`.trim() || row.customer.customerCompanyName || "") : "",
        "Issued By":    row.company?.companyName || "",
        "Trip Details": tripParts.join("; ") || "",
        "Amount":       parseFloat(row.quoteWorkOrderSaleGrandTotal || "0").toFixed(2),
        "Due Date":     row.quoteWorkOrderDueDate ? dayjs(row.quoteWorkOrderDueDate).format("DD MMM YYYY") : "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Quotes");
    XLSX.writeFile(wb, `quotes_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleOpenDeletePopup = (row: IQuoteWorkOrder) => {
    setCoursingToDelete(row);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteCustomerMutation();
  const [deleteWorkOrder] = useDeleteWorkOrderMutation();
  const [sendEmail] = useCreateEmailMutation();
  const [sendQuoteEmail] = useSendQuoteEmailMutation();
  const [convertToInvoice] = useConvertToInvoiceMutation();

  const handleOpenConvert = (row: IQuoteWorkOrder) => {
    setConvertRow(row);
    setConvertPoNumber("");
    setConvertHasPo(null);
    setOpenConvertDialog(true);
  };

  const handleConfirmConvert = async () => {
    if (!convertRow) return;
    setConvertLoading(true);
    try {
      const response = await convertToInvoice({
        id: String(convertRow.id),
        mainStatus: "2",
        po_number: convertPoNumber || "",
      }).unwrap();

      const newInvoiceId = (response as any).workOrder?.invoiceId || (response as any).data?.invoiceId || (response as any).invoiceId;

      if (!newInvoiceId) {
        showErrorMessage((response as any).error || "Failed to convert to invoice");
        return;
      }

      // If PO was provided, record the full payment automatically
      if (convertHasPo && convertPoNumber.trim()) {
        try {
          const grandTotal = String(convertRow.quoteWorkOrderSaleGrandTotal || "0");
          const formData = new FormData();
          formData.append("work_order_id", String(convertRow.id));
          formData.append("invoice_id", String(newInvoiceId));
          formData.append("customer_id", String(convertRow.quoteWorkOrderCustomerId || ""));
          formData.append("amount", grandTotal);
          formData.append("paymentDate", new Date().toLocaleDateString("en-CA"));
          formData.append("paymentMethod", "Purchase Order (PO)");
          formData.append("description", `PO Number: ${convertPoNumber}`);
          formData.append("payment_mode", "PO");
          const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
          await axios.post(`${API_URL}/save-payment`, formData, { headers });
          // Upload PO document if provided
          if (convertPoDocFile) {
            try {
              const poForm = new FormData();
              poForm.append("workOrderNo", convertRow.quoteWorkOrderNo || "");
              poForm.append("po_number", convertPoNumber);
              poForm.append("po_document", convertPoDocFile);
              await axios.post(`${API_URL}/update-po-number`, poForm, { headers });
            } catch {
              // Document upload non-critical
            }
          }
        } catch {
          // Payment recording non-critical — conversion still succeeded
        }
      }

      setOpenConvertDialog(false);
      setConvertRow(null);
      setConvertPoNumber("");
      setConvertHasPo(null);
      setConvertPoDocFile(null);
      navigate(`/work-order/history/${convertRow.id}`);
    } catch {
      showErrorMessage("Failed to convert to invoice");
    } finally {
      setConvertLoading(false);
    }
  };

  const handleOpenEmail = (row: IQuoteWorkOrder) => {
    setEmailRow(row);
    setEmailTo(row.customer?.customerEmail || row.customer?.customerCompanyEmail || "");
    setEmailSubject(`Your Quote is Ready – ${row.quoteWorkOrderNo}`);
    setEmailMessage(
      `Dear ${row.customer?.customerFirstName || "Customer"},\n\nYour quote ${row.quoteWorkOrderNo} is ready for your review.\n\nPlease let us know if you have any questions or would like to proceed.\n\nThank you for choosing us.\n\nBest regards`
    );
    setOpenEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!emailRow?.id) return;
    try {
      setEmailSending(true);
      await sendQuoteEmail({ work_order_id: emailRow.id, to: emailTo || undefined }).unwrap();
      showMessage("Email sent successfully");
      setOpenEmailDialog(false);
    } catch {
      showErrorMessage("Failed to send email");
    } finally {
      setEmailSending(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteWorkOrder(id).unwrap();
      if (response.status) {
        showMessage("Quote deleted successfully");
        setOpenDeleteCategory(false);
        setCoursingToDelete(null);
      } else {
        showErrorMessage("Failed to delete the quote");
      }
    } catch {
      showErrorMessage("Failed to delete the quote");
    }
  };

  const handleOpenViewQuote = (id: string) => { setQuoteId(id); setOpenViewQuote(true); };
  const handleCloseViewQuote = () => { setOpenViewQuote(false); setQuoteId(""); };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
        <CircularProgress />
      </Box>
    );

  // ─── Render ─────────────────────────────────────────────────────────────────

  const getWorkflowStep = (row: IQuoteWorkOrder) => {
    if (row.isInvoice) return 2; // Invoice
    if (row.quoteWorkOrderStatus === WorkOrderStatus.Pending) return 1; // Sent
    return 0; // Draft
  };

  const WorkflowBadge = ({ row }: { row: IQuoteWorkOrder }) => {
    const step = getWorkflowStep(row);
    const steps = [
      { label: "Draft", color: "#64748b", bg: "#f1f5f9", activeBg: "#f1f5f9" },
      { label: "Sent", color: "#2563eb", bg: "#dbeafe", activeBg: "#dbeafe" },
      { label: "Invoice", color: "#16a34a", bg: "#dcfce7", activeBg: "#dcfce7" },
    ];
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.6 }}>
        {steps.map((s, i) => {
          const active = i === step;
          const past = i < step;
          return (
            <React.Fragment key={s.label}>
              <Box sx={{
                fontSize: "0.65rem",
                fontWeight: active ? 700 : past ? 600 : 400,
                color: active ? s.color : past ? s.color + "99" : "#d1d5db",
                backgroundColor: active ? s.activeBg : "transparent",
                borderRadius: "20px",
                px: active ? 1 : 0.5,
                py: "1.5px",
                border: active ? `1px solid ${s.color}40` : "none",
                lineHeight: 1.5,
                whiteSpace: "nowrap",
                transition: "all 0.2s",
              }}>{active && "● "}{s.label}</Box>
              {i < steps.length - 1 && (
                <Box component="span" sx={{ color: i < step ? "#94a3b8" : "#e2e8f0", fontSize: "0.55rem", mx: 0.1 }}>›</Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  const statCards = [
    {
      label: "Total Quotes",
      value: stats.totalCount,
      icon: <FormatQuoteIcon sx={{ fontSize: 22 }} />,
      color: "#2563eb",
      bg: "#dbeafe",
    },
    {
      label: "Total Value",
      value: `$${stats.totalAmount.toFixed(2)}`,
      icon: <AttachMoneyIcon sx={{ fontSize: 22 }} />,
      color: "#16a34a",
      bg: "#dcfce7",
    },
    {
      label: "Open",
      value: stats.openCount,
      icon: <HourglassEmptyIcon sx={{ fontSize: 22 }} />,
      color: "#d97706",
      bg: "#fef3c7",
    },
    {
      label: "Collected",
      value: `$${stats.paidAmount.toFixed(2)}`,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />,
      color: "#2f3891",
      bg: "#ede9fe",
    },
  ];

  return (
    <>
      {/* ── Summary stats ─────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} lg={3} key={stat.label}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 2,
                boxShadow: "none",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: stat.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: stat.color,
                  flexShrink: 0,
                }}
              >
                {stat.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.25 }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ fontSize: "1.35rem", fontWeight: 700, color: "#1e293b", lineHeight: 1 }}>
                  {stat.value}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Main card ─────────────────────────────────────────────────────── */}
      <div>
        <Card sx={{ borderRadius: 2, boxShadow: "none", border: "1px solid #e2e8f0", overflow: "hidden" }}>

          {/* Card header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              borderBottom: "1px solid #e2e8f0",
              "@media print": { borderBottom: "none" },
            }}
          >
            {/* Title + count badge */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>
                Quotes
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#dbeafe",
                  color: "#2563eb",
                  borderRadius: "20px",
                  px: 1.5,
                  py: "2px",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  lineHeight: 1.6,
                }}
              >
                {filteredCoursings.length}
              </Box>
            </Box>

            {/* Controls */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
                "@media print": { display: "none" },
              }}
            >
              {/* Search */}
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch style={{ fontSize: 16, color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search quote, invoice, client..."
                size="small"
                sx={{ width: 200 }}
              />

              {/* Company filter */}
              <Select
                value={selectedCompany || ""}
                onChange={(e) => setSelectedCompany(e.target.value)}
                displayEmpty
                size="small"
                sx={{ height: 37, fontSize: "0.875rem", minWidth: 140 }}
              >
                <MenuItem value="">All Companies</MenuItem>
                {Array.isArray(companyData?.data) &&
                  companyData?.data.slice().sort((a: any, b: any) => (a.companyName || "").localeCompare(b.companyName || "")).map((c: any) => (
                    <MenuItem key={c.id} value={c.id}>
                      {capitalize(c.companyName)}
                    </MenuItem>
                  ))}
              </Select>

              {/* Date filter */}
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                displayEmpty
                size="small"
                sx={{ height: 37, fontSize: "0.875rem", minWidth: 130 }}
              >
                <MenuItem value="">All Dates</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="this_week">This Week</MenuItem>
                <MenuItem value="last_week">Last Week</MenuItem>
                <MenuItem value="this_month">This Month</MenuItem>
                <MenuItem value="last_month">Last Month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>

              {dateFilter === "custom" && (
                <>
                  <TextField
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 148 }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 148 }}
                  />
                </>
              )}

              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                sx={{
                  height: 37, fontSize: "0.875rem", borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                }}
              >
                <MenuItem value={50}>50 / page</MenuItem>
                <MenuItem value={100}>100 / page</MenuItem>
                <MenuItem value={0}>Show All</MenuItem>
              </Select>

              {filteredCoursings.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={pdfLoading ? <CircularProgress size={14} color="inherit" /> : <PrintIcon sx={{ fontSize: 16 }} />}
                    onClick={handleDownloadPdf}
                    disabled={pdfLoading}
                    sx={{
                      height: 37,
                      borderColor: "#e2e8f0",
                      color: "#475569",
                      fontWeight: 500,
                      "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
                    }}
                  >
                    {pdfLoading ? "Generating…" : "Print"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={handleExportToExcel}
                    sx={{
                      height: 37,
                      borderColor: "#e2e8f0",
                      color: "#16a34a",
                      fontWeight: 500,
                      "&:hover": { borderColor: "#bbf7d0", backgroundColor: "#f0fdf4" },
                    }}
                  >
                    Excel
                  </Button>
                </>
              )}

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/quote/create")}
                sx={{ height: 37, fontWeight: 600 }}
              >
                New Quote
              </Button>
            </Box>
          </Box>

          {/* ── Table ─────────────────────────────────────────────────────── */}
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 40 }}>
                    <CustomCheckbox name="checkbox" />
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: 50 }}>#</TableCell>
                  <TableCell sx={headerCellSx}>Quote / Invoice</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx}>Client</TableCell>
                  <TableCell sx={headerCellSx}>Issued By</TableCell>
                  <TableCell sx={headerCellSx}>Trip</TableCell>
                  <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>Amount</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell
                    sx={{
                      ...headerCellSx,
                      textAlign: "right",
                      "@media print": { display: "none" },
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isError || filteredCoursings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 7, border: 0 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                        <FormatQuoteIcon sx={{ fontSize: 44, color: "#cbd5e1" }} />
                        <Typography sx={{ fontWeight: 600, color: "#94a3b8", fontSize: "0.95rem" }}>
                          {isError ? "Error loading quotes" : "No quotes found"}
                        </Typography>
                        {!isError && (
                          <Typography sx={{ fontSize: "0.8rem", color: "#cbd5e1" }}>
                            Adjust your filters or create a new quote
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  (rowsPerPage === 0 ? filteredCoursings : filteredCoursings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
                    .map((row: IQuoteWorkOrder, index) => {
                      const invoice      = invoiceList.find((i) => String(i.id) === String(row.invoiceId) || String((i as any).work_order_id) === String(row.id));
                      const isConverted  = row.isInvoice || !!invoice;
                      const invoiceNo    = (invoice as any)?.InvoiceNo || row.InvoiceNo || null;
                      const invoiceAmt   = (invoice as any)?.quoteWorkOrderSaleGrandTotal || null;
                      const invoiceStatus = invoice ? (invoice as any).quoteWorkOrderStatus : null;
                      const activeStatus  = invoiceStatus != null ? invoiceStatus : row.quoteWorkOrderStatus;
                      const amountPaid    = parseFloat(String(row.amount_paid ?? (invoice as any)?.amount_paid ?? 0));
                      const grandTotal    = parseFloat((invoiceAmt || row.quoteWorkOrderSaleGrandTotal) || "0");
                      const balance       = Math.max(0, grandTotal - amountPaid);
                      // Derive display status from actual payment
                      const displayStatus = (grandTotal > 0 && amountPaid >= grandTotal)
                        ? WorkOrderStatus.Paid
                        : activeStatus;

                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:hover": { backgroundColor: "#fafcff" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                        >
                          {/* Checkbox */}
                          <TableCell sx={bodyCellSx}>
                            <CustomCheckbox name="checkbox" />
                          </TableCell>

                          {/* Row number */}
                          <TableCell sx={{ ...bodyCellSx, color: "#94a3b8", fontSize: "0.8rem" }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          {/* Quote / Invoice */}
                          <TableCell sx={{ ...bodyCellSx, whiteSpace: "nowrap" }}>
                            <Link to={`/work-order/history/${row.id}`} style={{ textDecoration: "none" }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                  color: "#2563eb",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {row.quoteWorkOrderNo}
                              </Typography>
                            </Link>
                            {invoiceNo && (
                              <Link to={`/invoice/history/${row.invoiceId}`} style={{ textDecoration: "none" }}>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.4,
                                    mt: 0.5,
                                    backgroundColor: "#dcfce7",
                                    color: "#16a34a",
                                    borderRadius: "20px",
                                    px: 1.25,
                                    py: "2px",
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    "&:hover": { backgroundColor: "#bbf7d0" },
                                  }}
                                >
                                  <DescriptionIcon sx={{ fontSize: 11 }} />
                                  {invoiceNo}
                                </Box>
                              </Link>
                            )}
                          </TableCell>

                          {/* Date */}
                          <TableCell sx={{ ...bodyCellSx, whiteSpace: "nowrap" }}>
                            <Typography sx={{ fontSize: "0.8rem", color: "#334155", fontWeight: 500 }}>
                              {dayjs(row.created_at).format("MMM D, YYYY")}
                            </Typography>
                            <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mt: 0.25 }}>
                              {dayjs(row.created_at).format("h:mm A")}
                            </Typography>
                          </TableCell>

                          {/* Client */}
                          <TableCell sx={bodyCellSx}>
                            <Link to={`/customer-history/${row.quoteWorkOrderCustomerId}`} style={{ textDecoration: "none" }}>
                              {row.customer?.customerCompanyName && (
                                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b", lineHeight: 1.4, "&:hover": { color: "#2563eb" } }}>
                                  {row.customer.customerCompanyName}
                                </Typography>
                              )}
                              <Typography
                                sx={{
                                  fontSize: "0.78rem",
                                  color: "#64748b",
                                  mt: row.customer?.customerCompanyName ? 0.25 : 0,
                                  "&:hover": { color: "#2563eb" },
                                }}
                              >
                                {capitalize(row.customer?.customerFirstName)}{" "}
                                {capitalize(row.customer?.customerLastName)}
                              </Typography>
                            </Link>
                          </TableCell>

                          {/* Issued By */}
                          <TableCell sx={{ ...bodyCellSx }}>
                            {row.company?.companyName ? (() => {
                              const name: string = row.company.companyName;
                              const isBusLines = name.toLowerCase().includes("bus line");
                              return (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: "6px",
                                    fontSize: "0.78rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.02em",
                                    whiteSpace: "nowrap",
                                    bgcolor: isBusLines ? "#dbeafe" : "#dcfce7",
                                    color: isBusLines ? "#1e40af" : "#15803d",
                                  }}
                                >
                                  {name}
                                </Box>
                              );
                            })() : "—"}
                          </TableCell>

                          {/* Trip */}
                          <TableCell sx={{ ...bodyCellSx, maxWidth: 260 }}>
                            {(() => {
                              const sales: any[] = Array.isArray(row.quoteWorkOrderSale) ? row.quoteWorkOrderSale : [];
                              if (!sales.length) return <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>—</Typography>;
                              return (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                                  {sales.slice(0, 2).map((sale: any, si: number) => {
                                    const firstPickup = Array.isArray(sale.pickups) && sale.pickups.length > 0 ? sale.pickups[0] : null;
                                    const tripDate = firstPickup?.dateTime || sale.departureDateTime;
                                    const from = (sale.transferTypeFrom || firstPickup?.city || "").trim();
                                    const to   = (sale.transferTypeTo   || "").trim();
                                    const busType = sale.productShortDescription || sale.productName || "";
                                    const isRound = String(sale.transferType) === "1";
                                    return (
                                      <Box key={si} sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                                        {/* Date */}
                                        {tripDate && dayjs(tripDate).isValid() && (
                                          <Typography sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 500 }}>
                                            {dayjs(tripDate).format("MMM D, YYYY · h:mm A")}
                                          </Typography>
                                        )}
                                        {/* Route */}
                                        {(from || to) && (
                                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {from}{from && to ? " → " : ""}{to}
                                          </Typography>
                                        )}
                                        {/* Tags row */}
                                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                          {busType && (
                                            <Box sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#475569", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "4px", px: 0.7, py: "1px", whiteSpace: "nowrap" }}>
                                              {busType}
                                            </Box>
                                          )}
                                          <Box sx={{ fontSize: "0.68rem", fontWeight: 600, color: isRound ? "#7c3aed" : "#0369a1", bgcolor: isRound ? "#ede9fe" : "#e0f2fe", border: `1px solid ${isRound ? "#ddd6fe" : "#bae6fd"}`, borderRadius: "4px", px: 0.7, py: "1px", whiteSpace: "nowrap" }}>
                                            {isRound ? "Round Trip" : "One Way"}
                                          </Box>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                  {sales.length > 2 && (
                                    <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8" }}>+{sales.length - 2} more leg{sales.length - 2 > 1 ? "s" : ""}</Typography>
                                  )}
                                </Box>
                              );
                            })()}
                          </TableCell>

                          {/* Amount */}
                          <TableCell sx={{ ...bodyCellSx, textAlign: "right", whiteSpace: "nowrap" }}>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
                              {formatAmt(grandTotal)}
                            </Typography>
                          </TableCell>

                          {/* Status */}
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              label={getStatusLabel(displayStatus, isConverted)}
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                height: 22,
                                bgcolor: getStatusBg(displayStatus, isConverted),
                                color: getStatusColor(displayStatus, isConverted),
                                border: "none",
                              }}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              textAlign: "right",
                              "@media print": { display: "none" },
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                              <Tooltip title="Send email to client" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEmail(row)}
                                  sx={{
                                    ...actionBtnSx,
                                    color: "#2f3891",
                                    backgroundColor: "#ede9fe",
                                    "&:hover": { backgroundColor: "#ddd6fe", color: "#252e7a" },
                                  }}
                                >
                                  <EmailOutlinedIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              {!isConverted && (
                                <Tooltip title="Convert to Invoice" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenConvert(row)}
                                    sx={{
                                      ...actionBtnSx,
                                      color: "#d97706",
                                      backgroundColor: "#fef3c7",
                                      "&:hover": { backgroundColor: "#fde68a", color: "#b45309" },
                                    }}
                                  >
                                    <NoteAddIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Preview quote" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenViewQuote(String(row.id))}
                                  sx={actionBtnSx}
                                >
                                  <DescriptionIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View details" arrow>
                                <IconButton
                                  size="small"
                                  component={Link as any}
                                  to={`/work-order/history/${row.id}`}
                                  sx={actionBtnSx}
                                >
                                  <VisibilitySharpIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={isConverted ? "Edit invoice" : "Edit quote"} arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/quote-history/${row.id}`)}
                                  sx={{
                                    ...actionBtnSx,
                                    color: isConverted ? "#16a34a" : "#2563eb",
                                    backgroundColor: isConverted ? "#dcfce7" : "#dbeafe",
                                    "&:hover": {
                                      backgroundColor: isConverted ? "#bbf7d0" : "#bfdbfe",
                                      color: isConverted ? "#15803d" : "#1d4ed8",
                                    },
                                  }}
                                >
                                  <EditSharpIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete quote" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeletePopup(row)}
                                  sx={{
                                    ...actionBtnSx,
                                    color: "#dc2626",
                                    backgroundColor: "#fee2e2",
                                    "&:hover": { backgroundColor: "#fecaca", color: "#b91c1c" },
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {rowsPerPage > 0 && <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid #e2e8f0",
              "@media print": { display: "none" },
            }}
          >
            <Pagination
              count={filteredCoursings.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
            />
          </Box>}
        </Card>
      </div>

      {/* ── View quote dialog ──────────────────────────────────────────────── */}
      {openViewQuote && (
        <Dialog
          open={openViewQuote}
          onClose={handleCloseViewQuote}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #e2e8f0",
              py: 2,
              px: 3,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>
              Quote Details
            </Typography>
            <IconButton size="small" onClick={handleCloseViewQuote} sx={{ color: "#64748b" }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <ViewWorkOrder quoteId={quoteId} type="view" />
          </DialogContent>
          <DialogActions sx={{ borderTop: "1px solid #e2e8f0", px: 3, py: 2 }}>
            <Button onClick={handleCloseViewQuote} variant="outlined" size="small" sx={{ fontWeight: 600 }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Send Email dialog ─────────────────────────────────────────────── */}
      <Dialog open={openEmailDialog} onClose={() => !emailSending && setOpenEmailDialog(false)}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

        {/* Gradient header */}
        <Box sx={{ background: "linear-gradient(135deg,#2f3891 0%,#252e7a 60%,#2f3891 100%)", px: 3, pt: 2.5, pb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <EmailOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff", lineHeight: 1.2 }}>Send Quote to Client</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.7)", mt: 0.2 }}>{emailRow?.quoteWorkOrderNo}</Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setOpenEmailDialog(false)} disabled={emailSending}
              sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, bgcolor: "#f8fafc" }}>
          {/* Client info strip */}
          <Box sx={{ bgcolor: "#fff", px: 3, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#2f3891" }}>
                {(emailRow?.customer?.customerFirstName?.[0] || "") + (emailRow?.customer?.customerLastName?.[0] || "")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1e293b" }}>
                {fmtCustomerName(emailRow?.customer)}
                {emailRow?.customer?.customerCompanyName && (
                  <Typography component="span" sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 400, ml: 0.75 }}>
                    · {emailRow.customer.customerCompanyName}
                  </Typography>
                )}
              </Typography>
              <TextField variant="standard" size="small" fullWidth value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)} placeholder="recipient@email.com"
                InputProps={{ disableUnderline: false, sx: { fontSize: "0.78rem", color: "#475569" } }}
                sx={{ mt: 0.25 }} />
            </Box>
          </Box>

          {/* Quick templates */}
          <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fff" }}>
            <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1 }}>Quick Templates</Typography>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {[
                { label: "Quote Ready", subject: `Your Quote is Ready – ${emailRow?.quoteWorkOrderNo}`, msg: `Dear ${emailRow?.customer?.customerFirstName || "Customer"},\n\nYour quote ${emailRow?.quoteWorkOrderNo} is ready for your review.\n\nPlease let us know if you have any questions or would like to proceed.\n\nBest regards` },
                { label: "Follow Up",   subject: `Following Up – ${emailRow?.quoteWorkOrderNo}`,         msg: `Dear ${emailRow?.customer?.customerFirstName || "Customer"},\n\nI wanted to follow up on quote ${emailRow?.quoteWorkOrderNo} that we sent recently.\n\nPlease let us know if you have any questions.\n\nBest regards` },
                { label: "Reminder",    subject: `Reminder – ${emailRow?.quoteWorkOrderNo}`,             msg: `Dear ${emailRow?.customer?.customerFirstName || "Customer"},\n\nThis is a friendly reminder regarding quote ${emailRow?.quoteWorkOrderNo}.\n\nPlease don't hesitate to contact us.\n\nBest regards` },
              ].map((t) => (
                <Box key={t.label} onClick={() => { setEmailSubject(t.subject); setEmailMessage(t.msg); }}
                  sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#2f3891", bgcolor: "#f5f3ff", border: "1px solid #ede9fe",
                    borderRadius: "20px", px: 1.25, py: "4px", cursor: "pointer", "&:hover": { bgcolor: "#ede9fe" }, transition: "background 0.15s" }}>
                  {t.label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Fields */}
          <Box sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 1.75 }}>
            <TextField label="Subject" size="small" fullWidth value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 1.5, "&:hover fieldset": { borderColor: "#a78bfa" }, "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: 2 } } }} />
            <TextField label="Message" size="small" fullWidth multiline rows={6} value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)} placeholder="Write your message here…"
              sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 1.5, "&:hover fieldset": { borderColor: "#a78bfa" }, "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: 2 } } }} />
            <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", textAlign: "right", mt: -1 }}>{emailMessage.length} characters</Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #e2e8f0", px: 3, py: 2, gap: 1, bgcolor: "#fff" }}>
          <Button onClick={() => setOpenEmailDialog(false)} disabled={emailSending} variant="outlined" size="small"
            sx={{ borderColor: "#e2e8f0", color: "#64748b", textTransform: "none", borderRadius: 1.5 }}>Cancel</Button>
          <Button onClick={handleSendEmail} variant="contained" size="small"
            disabled={!emailTo || !emailSubject || emailSending}
            startIcon={emailSending ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <EmailOutlinedIcon sx={{ fontSize: 15 }} />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, px: 2.5,
              background: "linear-gradient(135deg,#2f3891,#252e7a)", "&:hover": { background: "linear-gradient(135deg,#252e7a,#1e266b)" },
              "&:disabled": { background: "#e2e8f0", color: "#94a3b8" } }}>
            {emailSending ? "Sending…" : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Convert to Invoice dialog ──────────────────────────────────────── */}
      <Dialog open={openConvertDialog} onClose={() => setOpenConvertDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0", py: 2, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <NoteAddIcon sx={{ color: "#d97706", fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>Convert to Invoice</Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpenConvertDialog(false)} sx={{ color: "#64748b" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1, px: 3 }}>
          {convertHasPo === null && (
            <Box>
              <Typography sx={{ fontWeight: 600, color: "#1e293b", mb: 0.75 }}>
                Does this client have a Purchase Order (PO)?
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                {convertRow?.quoteWorkOrderNo} · {fmtCustomerName(convertRow?.customer)}
              </Typography>
            </Box>
          )}
          {convertHasPo === true && (
            <Box>
              <Typography sx={{ fontWeight: 600, color: "#1e293b", mb: 1.5 }}>Enter PO Number</Typography>
              <TextField size="small" fullWidth label="PO Number" autoFocus
                value={convertPoNumber} onChange={(e) => setConvertPoNumber(e.target.value)} />
              {/* PO Document Upload */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#475569", mb: 0.75 }}>
                  Attach PO Document <Typography component="span" sx={{ fontWeight: 400, color: "#94a3b8", fontSize: "0.78rem" }}>(optional)</Typography>
                </Typography>
                {convertPoDocFile ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #bbf7d0", borderRadius: 1.5, px: 1.5, py: 1, background: "#f0fdf4" }}>
                    <Box sx={{ fontSize: 18 }}>📄</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#15803d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {convertPoDocFile.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
                        {(convertPoDocFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setConvertPoDocFile(null)} sx={{ color: "#64748b" }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    component="label"
                    sx={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      border: "2px dashed #cbd5e1", borderRadius: 2, py: 2, px: 2, cursor: "pointer",
                      "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" },
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && f.size <= 10 * 1024 * 1024) setConvertPoDocFile(f);
                        e.target.value = "";
                      }}
                    />
                    <Box sx={{ fontSize: 22, mb: 0.5 }}>📎</Box>
                    <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>Click to upload PDF / image</Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>Max 10 MB</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {convertHasPo === false && (
            <Box sx={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 2, px: 2, py: 1.5 }}>
              <Typography variant="body2" sx={{ color: "#92400e" }}>
                ⚠️ The invoice will be created without a PO number. Payment will need to be collected separately.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e2e8f0", px: 3, py: 2, gap: 1 }}>
          {convertHasPo === null && (
            <>
              <Button onClick={() => setOpenConvertDialog(false)} variant="outlined" size="small"
                sx={{ borderColor: "#e2e8f0", color: "#64748b", textTransform: "none", mr: "auto" }}>Cancel</Button>
              <Button onClick={() => setConvertHasPo(false)} variant="outlined" size="small"
                sx={{ borderColor: "#fca5a5", color: "#dc2626", textTransform: "none",
                  "&:hover": { backgroundColor: "#fef2f2" } }}>No, I don't</Button>
              <Button onClick={() => setConvertHasPo(true)} variant="contained" size="small"
                sx={{ textTransform: "none", fontWeight: 600,
                  background: "linear-gradient(135deg,#16a34a,#15803d)" }}>Yes, I have a PO</Button>
            </>
          )}
          {convertHasPo === true && (
            <>
              <Button onClick={() => setConvertHasPo(null)} variant="text" size="small"
                sx={{ color: "#64748b", textTransform: "none", mr: "auto" }}>← Back</Button>
              <Button onClick={handleConfirmConvert} variant="contained" size="small"
                disabled={!convertPoNumber.trim() || convertLoading}
                sx={{ textTransform: "none", fontWeight: 600,
                  background: "linear-gradient(135deg,#1e293b,#0f172a)" }}>
                {convertLoading ? "Converting…" : "Convert & Mark Paid"}
              </Button>
            </>
          )}
          {convertHasPo === false && (
            <>
              <Button onClick={() => setConvertHasPo(null)} variant="text" size="small"
                sx={{ color: "#64748b", textTransform: "none", mr: "auto" }}>← Back</Button>
              <Button onClick={handleConfirmConvert} variant="contained" size="small"
                disabled={convertLoading}
                sx={{ textTransform: "none", fontWeight: 600,
                  background: "linear-gradient(135deg,#1e293b,#0f172a)" }}>
                {convertLoading ? "Converting…" : "Convert to Invoice"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      {coursingToDelete && (
        <DeletePopup
          open={openDeleteCategory}
          handleCloseDelete={handleCloseDeletePopup}
          onConfirm={async () => { await handleDelete(coursingToDelete.id.toString()); }}
          title="Delete Quote"
          content={`Are you sure you want to delete "${coursingToDelete.quoteWorkOrderNo}"?`}
        />
      )}
    </>
  );
};

export default Index;
